import { useEffect, useMemo, useState } from "react";
import { Trash2, Loader2, AlertCircle, CheckCircle2, Clock, Workflow } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useTemplates } from "@/hooks/useTemplates";
import { useFluxos } from "@/hooks/useFluxos";
import { BLOCK_META } from "@/components/fluxos/blockMeta";
import {
  useScheduledMessages,
  type ScheduledMessage,
  type ScheduledMessageStatus,
  type ScheduledKind,
} from "@/hooks/useScheduledMessages";
import { getDisplayName } from "@/utils/contactUtils";
import { MESSAGE_VARIABLES } from "@/lib/messageVariables";
import { getFriendlyScheduledMessageError } from "@/lib/friendlyErrors";
import type { Database } from "@/lib/supabase";
import { useTranslation } from "react-i18next";

type AccountContact = Database["public"]["Tables"]["account_contacts"]["Row"];

interface ScheduleMessageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: AccountContact | null;
}

const STATUS_LABEL: Record<ScheduledMessageStatus, string> = {
  pending: "Pendente",
  processing: "Enviando…",
  sent: "Enviada",
  failed: "Falhou",
  cancelled: "Cancelada",
};

const STATUS_CLASS: Record<ScheduledMessageStatus, string> = {
  pending: "bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/40",
  processing: "bg-[#EEF4FF] text-[#2563EB] border border-[#BFDBFE] dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/40",
  sent: "bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-green-500/15 dark:text-green-300 dark:border-green-500/40",
  failed: "bg-red-100 text-red-700 border border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/40",
  cancelled: "bg-[#F1F5F9] text-[#64748B] border border-[#E5E7EB] dark:bg-neutral-500/20 dark:text-neutral-300 dark:border-neutral-500/40",
};

function toLocalInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

function formatScheduledAt(iso: string): string {
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export const ScheduleMessageModal = ({
  open,
  onOpenChange,
  contact,
}: ScheduleMessageModalProps) => {
  const { t } = useTranslation("chat");
  const { templates, loading: templatesLoading } = useTemplates();
  const { fluxos, loading: fluxosLoading } = useFluxos();
  const {
    scheduledMessages,
    loading: scheduledLoading,
    error: scheduledError,
    createScheduledMessage,
    cancelScheduledMessage,
    deleteScheduledMessage,
    renderPreview,
  } = useScheduledMessages(contact?.id ?? null);

  const [kind, setKind] = useState<ScheduledKind>("fluxo");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedFluxoId, setSelectedFluxoId] = useState("");
  const [dateTime, setDateTime] = useState("");
  const [cancelOnReply, setCancelOnReply] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [previewText, setPreviewText] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const minDateTime = useMemo(() => {
    const d = new Date();
    d.setSeconds(0, 0);
    d.setMinutes(d.getMinutes() + 1);
    return toLocalInputValue(d);
  }, [open]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId]
  );

  const selectedFluxo = useMemo(
    () => fluxos.find((f) => f.id === selectedFluxoId) ?? null,
    [fluxos, selectedFluxoId]
  );

  const fluxoPreviewSummary = useMemo(() => {
    if (!selectedFluxo) return "";
    if (selectedFluxo.blocks.length === 0) return "Fluxo sem blocos";
    return selectedFluxo.blocks
      .map((b) => (BLOCK_META[b.type] ? t(`automations:${BLOCK_META[b.type].labelKey}`) : b.type))
      .join(" → ");
  }, [selectedFluxo]);

  useEffect(() => {
    let cancelled = false;
    if (kind !== "template" || !selectedTemplate || !contact) {
      if (kind !== "template") setPreviewText("");
      return () => {
        cancelled = true;
      };
    }
    renderPreview(selectedTemplate.content, contact.id).then((rendered) => {
      if (!cancelled) setPreviewText(rendered);
    });
    return () => {
      cancelled = true;
    };
  }, [kind, selectedTemplate, contact, renderPreview]);

  useEffect(() => {
    if (!open) {
      setKind("fluxo");
      setSelectedTemplateId("");
      setSelectedFluxoId("");
      setDateTime("");
      setCancelOnReply(true);
      setFormError(null);
      setSuccessMessage(null);
      setPreviewText("");
    }
  }, [open]);

  const isFutureEnough = useMemo(() => {
    if (!dateTime) return false;
    const target = new Date(dateTime);
    return target.getTime() > Date.now() + 30_000;
  }, [dateTime]);

  const canSubmit =
    !!contact &&
    !!dateTime &&
    isFutureEnough &&
    !submitting &&
    (kind === "template" ? !!selectedTemplate : !!selectedFluxo);

  const handleSave = async () => {
    if (!contact || !dateTime) return;
    setFormError(null);
    setSuccessMessage(null);

    if (!isFutureEnough) {
      setFormError("Escolha um horário pelo menos 1 minuto no futuro.");
      return;
    }

    setSubmitting(true);
    try {
      const localDate = new Date(dateTime);
      const created =
        kind === "fluxo"
          ? selectedFluxo
            ? await createScheduledMessage({
                kind: "fluxo",
                account_contact_id: contact.id,
                remote_jid: contact.jid,
                fluxo_id: selectedFluxo.id,
                blocks_snapshot: selectedFluxo.blocks,
                scheduled_at: localDate.toISOString(),
                cancel_on_reply: cancelOnReply,
              })
            : null
          : selectedTemplate
            ? await createScheduledMessage({
                kind: "template",
                account_contact_id: contact.id,
                remote_jid: contact.jid,
                template_id: selectedTemplate.id,
                content_snapshot: selectedTemplate.content,
                scheduled_at: localDate.toISOString(),
                cancel_on_reply: cancelOnReply,
              })
            : null;

      if (!created) {
        setFormError(scheduledError ?? "Erro ao agendar.");
        return;
      }

      const successText = `Agendamento criado para ${formatScheduledAt(created.scheduled_at)}.`;
      setSuccessMessage(successText);
      toast.success(successText);

      setSelectedTemplateId("");
      setSelectedFluxoId("");
      setDateTime("");
      setPreviewText("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (msg: ScheduledMessage) => {
    const wasPending = msg.status === "pending";

    if (wasPending) {
      const cancelled = await cancelScheduledMessage(msg.id);
      if (!cancelled.ok) {
        toast.error(cancelled.error ?? "Não foi possível cancelar o agendamento.");
        return;
      }
    }

    const removed = await deleteScheduledMessage(msg.id);
    if (!removed.ok) {
      toast.error(removed.error ?? "Não foi possível excluir o agendamento.");
      return;
    }

    toast.success(
      wasPending
        ? "Agendamento cancelado e excluído. A mensagem não será enviada."
        : "Registro removido do histórico."
    );
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const contactName = contact ? getDisplayName(contact) : t("schedule.contactLabel");

  const scheduledItemLabel = (msg: ScheduledMessage): string => {
    if ((msg.kind ?? "template") === "fluxo") {
      return (
        fluxos.find((f) => f.id === msg.fluxo_id)?.name ??
        t("schedule.flowLabel")
      );
    }
    return (
      templates.find((t) => t.id === msg.template_id)?.name ?? "Resposta rápida"
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-2xl shadow-md sm:max-w-[640px] max-h-[90dvh] overflow-y-auto dark:bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none dark:rounded-lg">
        <DialogHeader>
          <DialogTitle className="text-foreground text-lg dark:text-neutral-50">
            {t("schedule.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-5 pt-2">
          <div className="flex flex-col gap-2">
            <Label className="text-muted-foreground text-sm dark:text-neutral-300">{t("schedule.type")}</Label>
            <div className="flex rounded-xl border border-border p-1 bg-[#F8FAFC] dark:bg-neutral-800 dark:border-neutral-600 dark:rounded-lg">
              <button
                type="button"
                onClick={() => {
                  setKind("fluxo");
                  setSelectedTemplateId("");
                  setPreviewText("");
                  setFormError(null);
                  setSuccessMessage(null);
                }}
                className={`flex-1 h-9 rounded-[10px] text-sm font-medium transition-colors dark:rounded-md ${
                  kind === "fluxo"
                    ? "bg-white text-[#0F172A] shadow-sm dark:bg-neutral-700 dark:text-neutral-50"
                    : "text-[#64748B] hover:text-[#0F172A] dark:text-neutral-400 dark:hover:text-neutral-200"
                }`}
              >
                {t("schedule.flow")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setKind("template");
                  setSelectedFluxoId("");
                  setFormError(null);
                  setSuccessMessage(null);
                }}
                className={`flex-1 h-9 rounded-[10px] text-sm font-medium transition-colors dark:rounded-md ${
                  kind === "template"
                    ? "bg-white text-[#0F172A] shadow-sm dark:bg-neutral-700 dark:text-neutral-50"
                    : "text-[#64748B] hover:text-[#0F172A] dark:text-neutral-400 dark:hover:text-neutral-200"
                }`}
              >
                {t("schedule.quickReplies")}
              </button>
            </div>
          </div>

          {kind === "template" ? (
            <div className="flex flex-col gap-2">
              <Label className="text-muted-foreground text-sm dark:text-neutral-300">
                {t("schedule.quickReply")}
              </Label>
              <Select
                value={selectedTemplateId}
                onValueChange={setSelectedTemplateId}
                disabled={!contact}
              >
                <SelectTrigger className="w-full bg-white border-border text-foreground h-10 !rounded-xl dark:bg-neutral-700 dark:border-neutral-600 dark:text-neutral-50 dark:!rounded-[6px]">
                  <SelectValue
                    placeholder={
                      templatesLoading ? "Carregando..." : "Escolha a resposta rápida"
                    }
                  />
                </SelectTrigger>
                <SelectContent className="bg-card border-border dark:bg-neutral-700 dark:border-neutral-600">
                  {templatesLoading ? (
                    <div className="flex items-center justify-center py-3 gap-2 text-muted-foreground text-sm dark:text-neutral-400">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{t("schedule.loadingReplies")}</span>
                    </div>
                  ) : templates.length === 0 ? (
                    <div className="py-3 text-center text-muted-foreground text-sm dark:text-neutral-400">
                      {t("schedule.noReplies")}
                    </div>
                  ) : (
                    templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#64748B] dark:text-neutral-500">
                {t("schedule.repliesHint")}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <Label className="text-muted-foreground text-sm dark:text-neutral-300">{t("schedule.flow")}</Label>
              <Select
                value={selectedFluxoId}
                onValueChange={setSelectedFluxoId}
                disabled={!contact}
              >
                <SelectTrigger className="w-full bg-white border-border text-foreground h-10 !rounded-xl dark:bg-neutral-700 dark:border-neutral-600 dark:text-neutral-50 dark:!rounded-[6px]">
                  <SelectValue
                    placeholder={
                      fluxosLoading ? "Carregando..." : "Escolha o fluxo"
                    }
                  />
                </SelectTrigger>
                <SelectContent className="bg-card border-border dark:bg-neutral-700 dark:border-neutral-600">
                  {fluxosLoading ? (
                    <div className="flex items-center justify-center py-3 gap-2 text-muted-foreground text-sm dark:text-neutral-400">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{t("schedule.loadingFlows")}</span>
                    </div>
                  ) : fluxos.length === 0 ? (
                    <div className="py-3 text-center text-muted-foreground text-sm dark:text-neutral-400">
                      {t("schedule.noFlows")}
                    </div>
                  ) : (
                    fluxos.map((fluxo) => (
                      <SelectItem key={fluxo.id} value={fluxo.id}>
                        {fluxo.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#64748B] dark:text-neutral-500">
                {t("schedule.flowsHint")}
              </p>
            </div>
          )}

          {kind === "template" && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <Label className="text-muted-foreground text-sm dark:text-neutral-300">
                  {t("schedule.variables")}
                </Label>
                <span className="text-[11px] text-[#64748B] dark:text-neutral-500">
                  {t("schedule.editInSettings")}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {MESSAGE_VARIABLES.map((v) => (
                  <span
                    key={v.token}
                    title={v.description}
                    className="text-[11px] font-mono px-2 py-1 rounded-lg bg-[#EEF4FF] text-[#2563EB] border border-[#BFDBFE] dark:bg-neutral-700/70 dark:text-neutral-200 dark:border-neutral-600 dark:rounded"
                  >
                    {v.token}
                  </span>
                ))}
              </div>
            </div>
          )}

          {kind === "template" && selectedTemplate && (
            <div className="flex flex-col gap-2">
              <Label className="text-muted-foreground text-sm dark:text-neutral-300">
                {t("schedule.previewFor", { name: contactName })}
              </Label>
              <div className="bg-[#F8FAFC] border border-border rounded-xl p-3 text-sm text-foreground whitespace-pre-wrap min-h-[60px] dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-100 dark:rounded-md">
                {previewText || (
                  <span className="text-[#64748B] dark:text-neutral-500">
                    {t("schedule.loadingPreview")}
                  </span>
                )}
              </div>
            </div>
          )}

          {kind === "fluxo" && selectedFluxo && (
            <div className="flex flex-col gap-2">
              <Label className="text-muted-foreground text-sm dark:text-neutral-300">
                {t("schedule.flowPreview")}
              </Label>
              <div className="bg-[#F8FAFC] border border-border rounded-xl p-3 text-sm text-foreground dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-100 dark:rounded-md">
                <div className="flex items-center gap-2 mb-1.5">
                  <Workflow className="w-4 h-4 text-[#2563EB] dark:text-[#60A5FA]" />
                  <span className="font-medium">{selectedFluxo.name}</span>
                  <span className="text-[11px] text-[#64748B] dark:text-neutral-400">
                    {selectedFluxo.blocks.length}{" "}
                    {selectedFluxo.blocks.length === 1 ? "bloco" : "blocos"}
                  </span>
                </div>
                <p className="text-[#64748B] text-xs leading-relaxed dark:text-neutral-400">
                  {fluxoPreviewSummary}
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label className="text-muted-foreground text-sm dark:text-neutral-300">{t("schedule.whenToSend")}</Label>
            <Input
              type="datetime-local"
              value={dateTime}
              min={minDateTime}
              onChange={(e) => setDateTime(e.target.value)}
              className="bg-white border-border text-foreground h-10 rounded-xl [color-scheme:light] dark:bg-neutral-700 dark:border-neutral-600 dark:text-neutral-50 dark:rounded-[6px] dark:[color-scheme:dark]"
              placeholder={t("schedule.datePlaceholder")}
            />
            {dateTime && !isFutureEnough && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {t("schedule.futureRequired")}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-[#F8FAFC] px-3 py-2.5 dark:bg-neutral-800/60 dark:border-neutral-600 dark:rounded-lg">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground dark:text-neutral-100">
                {t("schedule.cancelOnReply")}
              </p>
              <p className="text-[11px] text-[#64748B] dark:text-neutral-400 mt-0.5">
                {t("schedule.cancelOnReplyHint")}
              </p>
            </div>
            <Switch
              checked={cancelOnReply}
              onCheckedChange={setCancelOnReply}
              className="shrink-0"
            />
          </div>

          {formError && (
            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-2 dark:text-red-300 dark:bg-red-500/10 dark:border-red-500/40 dark:rounded-md">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          {successMessage && (
            <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl p-2 dark:text-green-300 dark:bg-green-500/10 dark:border-green-500/40 dark:rounded-md">
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold text-[#2563EB] dark:text-[#60A5FA]">
              {t("schedule.listTitle")}
            </h3>

            {scheduledLoading ? (
              <p className="text-muted-foreground text-sm text-center py-4 flex items-center justify-center gap-2 dark:text-neutral-400">
                <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading")}
              </p>
            ) : scheduledMessages.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-4 dark:text-neutral-400">
                {t("schedule.listEmpty")}
              </p>
            ) : (
              <div className="border border-border rounded-xl overflow-hidden dark:border-neutral-700 dark:rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-[#F8FAFC] dark:border-neutral-700 dark:bg-neutral-800/50">
                      <th className="text-left px-3 py-2 text-[#64748B] font-medium dark:text-neutral-400">
                        {t("schedule.colItem")}
                      </th>
                      <th className="text-left px-3 py-2 text-[#64748B] font-medium dark:text-neutral-400">
                        {t("schedule.colDateTime")}
                      </th>
                      <th className="text-left px-3 py-2 text-[#64748B] font-medium dark:text-neutral-400">
                        {t("schedule.colStatus")}
                      </th>
                      <th className="w-10 px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduledMessages.map((msg) => {
                      const label = scheduledItemLabel(msg);
                      const isFluxo = (msg.kind ?? "template") === "fluxo";
                      return (
                        <tr
                          key={msg.id}
                          className="border-b border-border/60 last:border-b-0 hover:bg-[#F8FAFC] transition-colors duration-200 align-top dark:border-neutral-700/50 dark:hover:bg-neutral-700/30"
                        >
                          <td className="px-3 py-2.5 text-foreground dark:text-neutral-200">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-medium">{label}</span>
                              {isFluxo && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EEF4FF] text-[#2563EB] border border-[#BFDBFE] dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/40">
                                  {t("schedule.flow")}
                                </span>
                              )}
                            </div>
                            {msg.status === "failed" && msg.error_message && (
                              <div
                                className="text-[11px] text-red-600 mt-0.5 flex items-start gap-1 cursor-help dark:text-red-300"
                                title={msg.error_message}
                              >
                                <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                <span>
                                  {getFriendlyScheduledMessageError(
                                    msg.error_message
                                  )}
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-foreground whitespace-nowrap dark:text-neutral-200">
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3 h-3 text-[#2563EB] dark:text-neutral-400" />
                              {formatScheduledAt(msg.scheduled_at)}
                            </div>
                            {msg.status === "sent" && msg.sent_at && (
                              <div className="text-[11px] text-emerald-600 mt-0.5 flex items-center gap-1 dark:text-green-400">
                                <CheckCircle2 className="w-3 h-3" />
                                Enviada {formatScheduledAt(msg.sent_at)}
                              </div>
                            )}
                            <div className="text-[11px] text-[#64748B] mt-0.5 dark:text-neutral-500">
                              {msg.cancel_on_reply !== false
                                ? "Cancela se o contato responder"
                                : "Não cancela se o contato responder"}
                            </div>
                          </td>
                          <td className="px-3 py-2.5">
                            <span
                              className={`text-[11px] px-2 py-0.5 rounded-full ${
                                STATUS_CLASS[msg.status]
                              }`}
                            >
                              {STATUS_LABEL[msg.status]}
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            {msg.status !== "processing" && (
                              <button
                                onClick={() => handleRemove(msg)}
                                className="text-muted-foreground hover:text-red-500 transition-colors duration-200 p-1 rounded-lg hover:bg-red-50 dark:text-neutral-400 dark:hover:text-red-400 dark:hover:bg-neutral-600/50 dark:rounded"
                                aria-label={
                                  msg.status === "pending"
                                    ? "Cancelar agendamento"
                                    : "Remover do histórico"
                                }
                                title={
                                  msg.status === "pending"
                                    ? "Cancelar agendamento"
                                    : "Remover do histórico"
                                }
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="bg-transparent border-border text-foreground hover:bg-[#F1F5F9] dark:border-neutral-600 dark:text-neutral-300 dark:hover:bg-neutral-700 dark:hover:text-neutral-50"
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!canSubmit}
              className="bg-[#2563EB] text-white hover:bg-[#1D4ED8] disabled:opacity-50"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> {t("schedule.saving")}
                </span>
              ) : (
                t("schedule.save")
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
